import {
  AgentMappingEnum,
  ClientMappingEnum,
  InsuranceCompanyMappingEnum,
  OfficeMappingEnum,
  NewPolicyMappingEnum,
  PolicyMappingEnum,
  UserMappingEnum,
  VehicleMappingEnum,
  AnnexMappingEnum,
  InstallmentMappingEnum,
  PolicyFullMappingEnum,
  PolicyValuesMappingEnum,
  CommissionMappingEnum,
  ClientPolicyMappingEnum,
  PolicyReportMappingEnum,
  DocumentSearchMappingEnum,
  DocumentLogMappingEnum,
  BrokerReportMappingEnum,
  DailyReportMappingEnum,
  SummaryReportMappingEnum,
  AgentReportMappingEnum,
  BankReportMappingEnum,
  VehiclePolicyMappingEnum,
  PolicySaveMappingEnum,
  ClientSaveResultMappingEnum,
} from "../enum/normalization-mapping.enum.ts";
import { ClientDataType } from "../types/client.ts";

const normalizeProperties = (
  object: object,
  mapping: object,
  reverse = false
) => {
  const normalizedObj: any = {};
  const finalMapping = reverse
    ? Object.fromEntries(
        Object.entries(mapping).map(([key, value]) => [value, key])
      )
    : mapping;

  Object.keys(object).forEach((key) => {
    const normalizedKey = finalMapping[key];
    if (normalizedKey) {
      normalizedObj[normalizedKey] = object[key];
    }
  });

  return normalizedObj;
};

export const normalizeNewPolicyProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, NewPolicyMappingEnum, reverse);

export const normalizePolicyProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, PolicyMappingEnum, reverse);

export const normalizePolicyReportProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, PolicyReportMappingEnum, reverse);

export const normalizeAgentReportProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, AgentReportMappingEnum, reverse);

export const normalizeBrokerReportProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, BrokerReportMappingEnum, reverse);

export const normalizeDailyReportProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, DailyReportMappingEnum, reverse);

export const normalizeSummaryReportProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, SummaryReportMappingEnum, reverse);

export const normalizeDocumentSearchProperties = (
  documentObject: object,
  reverse = false
) => normalizeProperties(documentObject, DocumentSearchMappingEnum, reverse);

export const normalizeDocumentLogProperties = (
  logObject: object,
  reverse = false
) => normalizeProperties(logObject, DocumentLogMappingEnum, reverse);

export const normalizePolicyFullProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, PolicyFullMappingEnum, reverse);

export const normalizePolicySaveProperties = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, PolicySaveMappingEnum, reverse);

export const normalizeClientPolicyValues = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, ClientPolicyMappingEnum, reverse);

export const normalizeVehiclePolicyValues = (
  policyObject: object,
  reverse = false
) => normalizeProperties(policyObject, VehiclePolicyMappingEnum, reverse);

export const normalizeAnnexValues = (annexObject: object, reverse = false) =>
  normalizeProperties(annexObject, AnnexMappingEnum, reverse);

export const normalizePolicyValues = (
  policyValuesObject: object,
  reverse = false
) => normalizeProperties(policyValuesObject, PolicyValuesMappingEnum, reverse);

export const normalizeInstallmentProperties = (
  installmentObject: object,
  reverse = false
) => normalizeProperties(installmentObject, InstallmentMappingEnum, reverse);

export const normalizeOfficeProperties = (
  officeObject: object,
  reverse = false
) => normalizeProperties(officeObject, OfficeMappingEnum, reverse);

export const normalizeInsuranceCompanyProperties = (
  companyObject: object,
  reverse = false
) => normalizeProperties(companyObject, InsuranceCompanyMappingEnum, reverse);

export const normalizeVehicleProperties = (
  vehicleObject: object,
  reverse = false
) => normalizeProperties(vehicleObject, VehicleMappingEnum, reverse);

export const normalizeBankReportProperties = (
  bankReportObject: object,
  reverse = false
) => normalizeProperties(bankReportObject, BankReportMappingEnum, reverse);

export const normalizeClientProperties = (
  clientObject: object,
  reverse = false
): ClientDataType =>
  normalizeProperties(clientObject, ClientMappingEnum, reverse);

export const normalizeClientSaveResponseProperties = (
  clientSaveResponseObject: object
): ClientDataType =>
  normalizeProperties(clientSaveResponseObject, ClientSaveResultMappingEnum);

export const normalizeUserProperties = (userObject: object, reverse = false) =>
  normalizeProperties(userObject, UserMappingEnum, reverse);

export const normalizeAgentProperties = (
  agentObject: object,
  reverse = false
) => normalizeProperties(agentObject, AgentMappingEnum, reverse);

export const normalizeCommissionProperties = (
  commissionObject: object,
  reverse = false
) => normalizeProperties(commissionObject, CommissionMappingEnum, reverse);

export const normalizeDate = (dateString: string) => {
  if (!dateString || dateString.trim().length !== 8) {
    return;
  }

  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  return `${day}/${month}/${year}`;
};

export const revertNormalizeDate = (dateString: string) => {
  const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateString.match(datePattern);

  if (match) {
    const [, day, month, year] = match;
    return `${year}${month}${day}`;
  }

  return dateString;
};

export const findKeyByValue = (
  map: { [key: string]: string },
  value: string
): string => {
  return (
    Object.entries(map).find(
      ([, v]) => v.trim().toLowerCase() === value?.trim().toLowerCase()
    )?.[0] ?? value
  );
};
